/**
 * The operator screen's alert bar (Marina-style, phase 7b-C's tenant
 * alerts): which of the tenant's active alerts belong on THIS production's
 * screen, which the operator has hidden for themselves, and when a critical
 * one is new enough to sound the cue.
 *
 *   relevant       active, not acknowledged for everyone, and about this
 *                  production's place: its studio's alerts, and its site's
 *                  alerts that name no studio (a node offline, a certificate
 *                  expiring); never another studio's
 *   hidden for me  "for me" hides one episode in this browser (`hideKey`:
 *                  the key and when it was raised) — a re-raise shows again
 *   the cue        a critical alert not seen on this screen before
 */
import type { AlertWire } from '@acp/api-contract/alerts.js';
import { severityRank } from '@acp/tenant-alerts/catalog.js';

/** Where the production runs. */
export type Place = { readonly siteId: string | null; readonly studioId: string | null };

/** This browser's "hide for me" list, in storage (one list for every production: keys are tenant-unique). */
export const HIDDEN_ALERTS_STORAGE_KEY = 'acp.hiddenAlerts';

/** Whether the critical-alert cue is on, in storage (`on` / `off`). */
export const ALERT_SOUND_STORAGE_KEY = 'acp.alertSound';

/** One episode of an alert: what "hide for me" remembers. */
export function hideKey(alert: Pick<AlertWire, 'key' | 'raisedAt'>): string {
  return `${alert.key}@${alert.raisedAt}`;
}

/** A studio's alert by its studio (tenant-unique); a site-wide one by its site — none for a production without a site. */
export function aboutPlace(alert: Pick<AlertWire, 'siteId' | 'studioId'>, place: Place): boolean {
  if (alert.studioId !== null) return alert.studioId === place.studioId;
  return place.siteId !== null && alert.siteId === place.siteId;
}

/** The alerts the bar shows, most severe first, then newest. */
export function barAlerts(alerts: readonly AlertWire[], place: Place, hidden: readonly string[]): AlertWire[] {
  const hide = new Set(hidden);
  return alerts
    .filter((a) => a.status === 'active' && a.acknowledged === null && aboutPlace(a, place) && !hide.has(hideKey(a)))
    .sort((a, b) => severityRank(b.severity) - severityRank(a.severity) || (a.raisedAt < b.raisedAt ? 1 : a.raisedAt > b.raisedAt ? -1 : 0));
}

/**
 * The cue's decision: whether any critical alert on the bar is new since `seen` (the episodes already heard on this
 * screen), and the episodes seen now. The first look at a screen hears nothing — an operator opening it mid-show
 * is not greeted by a siren for what was already there — unless `seen` is given.
 */
export function criticalCue(shown: readonly AlertWire[], seen: ReadonlySet<string> | null): { readonly sound: boolean; readonly seen: ReadonlySet<string> } {
  const now = new Set(shown.filter((a) => a.severity === 'critical').map(hideKey));
  if (seen === null) return { sound: false, seen: now };
  const sound = [...now].some((key) => !seen.has(key));
  return { sound, seen: new Set([...seen, ...now]) };
}
